// @ts-strict-ignore
import { BoxGeometry, Material, Mesh, Object3D, Vector3 } from 'three';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { MetricUtils } from '@3d-scene/common/util/metric-utils';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ProjectModel } from '@project/model/project.model';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { ProjectArticleService } from '@project/service/project-article.service';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

export class BaseZoneArticlePostParseProcessor {
  private readonly DEFAULT_HEIGHT: number = 3;
  // to support old projects with blocking zones which don't have articleDepth property
  private readonly DEFAULT_DEPTH: number = 350;

  constructor(private containerService: ContainerService) {}

  protected updateUsageProperties(articleUsage: ArticleUsageVO, projectModel: ProjectModel): void {
    // Override this method
  }

  public process(articleContainer: ArticleContainer, renderOrder: number = 0): void {
    if (articleContainer.articleObject3DDataShared) {
      throw new Error('Modifying a shared article geometry is not allowed!');
    }

    const projectArticleService: ProjectArticleService = articleContainer.projectArticleService;
    const articleUsage: ArticleUsageVO = articleContainer.articleUsage;
    this.updateUsageProperties(articleUsage, projectArticleService.projectModel);

    const articleLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_LENGTH);
    let articleDepth: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_DEPTH);
    if (!NumberUtils.isNumber(articleDepth)) {
      // process/resize blocking zone by existing mounting rail container
      const mountingRailContainer: ArticleContainer = this.findFirstMountingRailContainer(projectArticleService);
      if (mountingRailContainer) {
        articleDepth = this.calculateZoneDepthByMountingRailContainer(mountingRailContainer);
      } else {
        // this case occurs when blocking zone from loaded project doesn't have 'articleDepth'
        // property and mounting rail container wasn't loaded yet
        // this may happen only in old/existing projects (before introducing blocking zones with 'articleDepth' property)
        articleDepth = this.DEFAULT_DEPTH;
      }
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(articleUsage, ArticleUsageInfoPropertyDef.ARTICLE_DEPTH, articleDepth);
    }
    const article: ArticleVO = articleContainer.article;
    this.updateBlockingZoneGeometry(articleContainer, articleLength, articleDepth, article.metaElements, renderOrder);
  }

  private calculateZoneDepthByMountingRailContainer(mountingRailContainer: ArticleContainer): number {
    const realSize: Vector3 = MetricUtils.getRealSize(mountingRailContainer.model);
    // consider that mounting rail aligned by -x axis and depth of mounting rail is aligned with z-axis
    return realSize.z;
  }

  private updateBlockingZoneGeometry(
    articleContainer: ArticleContainer,
    articleLength: number,
    articleDepth: number,
    connectionAreas: MetaElementVO[],
    renderOrder: number
  ): void {
    const connectionNames: string[] = [];
    for (const metaElement of connectionAreas) {
      connectionNames.push(metaElement.name);
    }
    const material: Material = Object3DUtils.findFirstMaterial(articleContainer, connectionNames);
    this.updateConnectionMeshes(articleContainer.model, articleContainer, connectionNames, articleLength);
    const zoneGeometry: BoxGeometry = new BoxGeometry(articleLength, this.DEFAULT_HEIGHT, articleDepth);
    const zoneMesh: Mesh = new Mesh(zoneGeometry, material);
    // Set the rendering order to make drilling holes visible threw this mesh
    zoneMesh.renderOrder = renderOrder;
    const givingArea: ConnectionAreaVO = ArticleUtils.findFirstConnectionArea(connectionAreas, ConnectionAreaVO.SIDE_GIVING);
    if (givingArea) {
      zoneGeometry.translate(-(articleLength / 2), this.DEFAULT_HEIGHT / 2 + CommonConstants.EPSILON, 0);
    }
    const meshes: Mesh[] = MeshUtils.findGeometryMeshes(articleContainer.model);
    for (const mesh of meshes) {
      articleContainer.model.remove(mesh);
    }
    articleContainer.addChildToModel(zoneMesh);
  }

  private findFirstMountingRailContainer(projectArticleService: ProjectArticleService): ArticleContainer {
    const mountingRailUsages: ArticleUsageVO[] = projectArticleService.findArticleUsagesByType(ArticleTypeConstants.TYPE_MOUNTING_RAIL);
    let mountingRailContainer: ArticleContainer = null;
    if (mountingRailUsages.length > 0) {
      mountingRailContainer = this.containerService.findArticleContainerInGlobal(mountingRailUsages[0].id);
    }
    return mountingRailContainer;
  }

  private updateConnectionMeshes(model3d: Object3D, baseContainer: ArticleContainer, connectionNames: string[], stretchLength: number): void {
    const children: Object3D[] = Object3DUtils.getChildren(model3d);
    for (const child of children) {
      this.updateConnectionMeshes(child, baseContainer, connectionNames, stretchLength);
      if (IsThreeJsType.mesh(child)) {
        if (connectionNames.indexOf(child.name) >= 0) {
          const connection: Connection = baseContainer.findConnectionByName(child.name);
          if (connection != null) {
            const connectionMesh: Mesh = ArticlesCreator.stretchConnection(connection, stretchLength, true);
            if (connectionMesh !== null) {
              baseContainer.removeChildFromModel(child);
              Object3DUtils.destroy(child);
              baseContainer.addChildToModel(connectionMesh);
            }
          }
        } else {
          baseContainer.removeChildFromModel(child);
          Object3DUtils.destroy(child);
        }
      }
    }
  }
}
